import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { tasksAPI } from '../services/api';
import TaskLogs from './TaskLogs';
import AttachmentView from './AttachmentView';
import { formatTaskTypeLabel } from '../data/taskTypes';

// Helper function to format time in minutes to readable format
const formatTime = (minutes) => {
  if (!minutes) return 'N/A';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0 && mins > 0) {
    return `${hours} hour${hours > 1 ? 's' : ''} ${mins} minute${mins > 1 ? 's' : ''}`;
  } else if (hours > 0) {
    return `${hours} hour${hours > 1 ? 's' : ''}`;
  } else {
    return `${mins} minute${mins > 1 ? 's' : ''}`;
  }
};

const TaskDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusUpdate, setStatusUpdate] = useState('');

  const loadTask = useCallback(async () => {
    try {
      const response = await tasksAPI.getTask(id);
      setTask(response.data);
      setStatusUpdate(response.data.status);
    } catch (err) {
      setError('Failed to load task');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadTask();
  }, [loadTask]);

  const handleStatusUpdate = async () => {
    try {
      await tasksAPI.updateTaskStatus(id, statusUpdate);
      loadTask();
    } catch (err) {
      alert('Failed to update status');
    }
  };

  if (loading) {
    return <div style={styles.loading}>Loading task...</div>;
  }

  if (error || !task) {
    return <div style={styles.error}>{error || 'Task not found'}</div>;
  }

  return (
    <div className="main-content">
      <div style={styles.header}>
        <button onClick={() => navigate('/tasks')} className="btn btn-secondary">
          ← Back to Tasks
        </button>
        <button
          onClick={() => navigate(`/tasks/${id}/edit`)}
          className="btn btn-primary"
        >
          Edit Task
        </button>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">{task.title}</h2>
        </div>

        <div style={styles.statusSection}>
          <label className="form-label">Update Status:</label>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
            <select
              value={statusUpdate}
              onChange={(e) => setStatusUpdate(e.target.value)}
              className="form-select"
              style={{ flex: 1, maxWidth: '200px' }}
            >
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
            </select>
            <button onClick={handleStatusUpdate} className="btn btn-success">
              Update Status
            </button>
          </div>
        </div>

        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Details</h3>
          <div style={styles.detailsGrid}>
            {task.description && (
              <div style={styles.detailItem}>
                <strong>Description:</strong>
                <p style={styles.text}>{task.description}</p>
              </div>
            )}
            {task.task_type && (
              <div style={styles.detailItem}>
                <strong>Type:</strong> {formatTaskTypeLabel(task.task_type)}
              </div>
            )}
            {task.location && (
              <div style={styles.detailItem}>
                <strong>Location:</strong> {task.location}
              </div>
            )}
            {task.ideal_outcome && (
              <div style={styles.detailItem}>
                <strong>Ideal Outcome:</strong>
                <p style={styles.text}>{task.ideal_outcome}</p>
              </div>
            )}
            <div style={styles.detailItem}>
              <strong>Priority:</strong> <span className={`badge ${task.priority === 'high' ? 'badge-danger' : task.priority === 'medium' ? 'badge-warning' : 'badge-success'}`}>{task.priority}</span>
            </div>
            <div style={styles.detailItem}>
              <strong>Status:</strong> <span className={`badge ${task.status === 'completed' ? 'badge-success' : task.status === 'in_progress' ? 'badge-warning' : 'badge-info'}`}>{task.status.replace('_', ' ')}</span>
            </div>
            {task.category && (
              <div style={styles.detailItem}>
                <strong>Category:</strong> {task.category}
              </div>
            )}
            {task.allocated_to_username && (
              <div style={styles.detailItem}>
                <strong>Allocated To:</strong> {task.allocated_to_username}
              </div>
            )}
            <div style={styles.detailItem}>
              <strong>Created:</strong> {new Date(task.created_at).toLocaleString()}
            </div>
            {task.completed_at && (
              <div style={styles.detailItem}>
                <strong>Completed:</strong> {new Date(task.completed_at).toLocaleString()}
              </div>
            )}
            {task.total_time_taken > 0 && (
              <div style={styles.detailItem}>
                <strong>Total Time Spent:</strong> {formatTime(task.total_time_taken)}
              </div>
            )}
            {task.time_to_completion && (
              <div style={styles.detailItem}>
                <strong>Time to Completion:</strong> {formatTime(task.time_to_completion)}
              </div>
            )}
          </div>
        </div>

        <AttachmentView taskId={id} />

        <TaskLogs taskId={id} onLogAdded={loadTask} />
      </div>
    </div>
  );
};

const styles = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '1.5rem',
  },
  statusSection: {
    marginBottom: '2rem',
    padding: '1.25rem',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
  },
  section: {
    marginBottom: '2rem',
    paddingTop: '1.5rem',
    borderTop: '1px solid #e5e7eb',
  },
  sectionTitle: {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '1rem',
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '1rem',
  },
  detailItem: {
    padding: '0.75rem',
    backgroundColor: '#f8f9fa',
    borderRadius: '6px',
  },
  text: {
    color: '#475569',
    lineHeight: '1.6',
    margin: '0.5rem 0 0 0',
  },
  loading: {
    textAlign: 'center',
    padding: '4rem 2rem',
    color: '#64748b',
  },
  error: {
    color: '#991b1b',
    textAlign: 'center',
    padding: '2rem',
    backgroundColor: '#fee2e2',
    borderRadius: '8px',
    margin: '2rem',
  },
};

export default TaskDetail;
